// GuestIdUpload.js — the guest's page: /guest-id?t=<token>
//
// Opened from the WhatsApp link or the QR at the desk, on the guest's own
// phone, with no login. The guest picks the room they are staying in, adds a
// photo of their ID, and taps Upload. An Aadhaar is read automatically; the
// name and number are shown back so a wrong read can be fixed on the spot.
// The link lives 30 minutes and belongs to one booking only.
//
// Written for someone standing in a lobby with a phone: one thing per screen,
// big buttons, plain words, no hotel jargon.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Button, TextField, MenuItem, CircularProgress,
  Stack, Chip, Alert, LinearProgress,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BedOutlinedIcon from '@mui/icons-material/BedOutlined';
import config from '../../config';

const GREEN = {
  backgroundImage: 'none !important', bgcolor: 'var(--hd-success, #047857)', color: '#fff !important',
  textTransform: 'none', fontWeight: 800, fontSize: '1rem', py: 1.4, borderRadius: 2,
  '&:hover': { backgroundImage: 'none !important', bgcolor: '#065f46' },
  '&.Mui-disabled': { bgcolor: '#CBD5E1', color: '#fff !important' },
};
const PLAIN = {
  backgroundImage: 'none !important', bgcolor: 'var(--hd-surface, #ffffff)', color: 'var(--hd-text-primary, #0f172a) !important',
  textTransform: 'none', fontWeight: 700, borderColor: 'var(--hd-border, #cbd5e1)',
  '&:hover': { backgroundImage: 'none !important', bgcolor: 'var(--hd-page-bg, #f1f5f9)', borderColor: '#94A3B8' },
};

const minutesLeft = (expiresAt) => {
  if (!expiresAt) return 0;
  const ms = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 60000));
};

// The page frame. Defined at module level on purpose: declared inside the
// component its identity would change every render, React would remount the
// whole page each time, and the name box would lose focus after one letter.
function Shell({ children }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'var(--hd-page-bg, #f1f5f9)', py: { xs: 2, sm: 4 }, px: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 520, mx: 'auto', p: { xs: 2, sm: 3 }, borderRadius: 3, border: '1px solid var(--hd-border, #e2e8f0)' }}>
        {children}
      </Paper>
    </Box>
  );
}

// A photo picker that opens the camera on a phone and the file dialog on a PC.
function PhotoPick({ label, file, onPick, required }) {
  const ref = useRef(null);
  return (
    <Box>
      <input
        ref={ref} type='file' hidden accept='image/*,application/pdf' capture='environment'
        onChange={(e) => { onPick(e.target.files?.[0] || null); e.target.value = ''; }}
      />
      <Button fullWidth variant='outlined' startIcon={file ? <CheckCircleIcon /> : <CameraAltIcon />}
        onClick={() => ref.current?.click()}
        sx={{ ...PLAIN, py: 1.2, justifyContent: 'flex-start', borderColor: file ? 'var(--hd-success, #047857)' : 'var(--hd-border, #cbd5e1)',
          color: file ? 'var(--hd-success, #047857) !important' : 'var(--hd-text-primary, #0f172a) !important' }}>
        {file ? `${label}: ${file.name.length > 26 ? `${file.name.slice(0, 24)}…` : file.name}` : `${label}${required ? '' : ' (optional)'}`}
      </Button>
    </Box>
  );
}

export default function GuestIdUpload() {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('t') || '', []);
  const api = config.API_BASE_URL;

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);       // what the server says about the link
  const [dead, setDead] = useState('');         // reason when the link cannot be used
  const [tick, setTick] = useState(0);          // re-render the countdown each minute

  const [room, setRoom] = useState('');         // '' until the guest picks (or 0 = no room)
  const [idType, setIdType] = useState('Aadhar Card');
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [name, setName] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [last, setLast] = useState(null);       // the ID just saved, for the "check it" card
  const [fix, setFix] = useState(null);         // { name, idNumber } while correcting

  const load = useCallback(async () => {
    if (!token) { setDead('This link is not valid.'); setLoading(false); return; }
    try {
      const r = await fetch(`${api}/guestid/${encodeURIComponent(token)}`);
      const d = r.ok ? await r.json() : null;
      if (!d || d.valid === false) { setDead(d?.reason || 'This link is not valid.'); setInfo(null); }
      else {
        setInfo(d);
        setDead('');
        // One room: no choice to make. Several: the guest picks.
        if (Array.isArray(d.rooms) && d.rooms.length === 1) setRoom(String(d.rooms[0].roomNumber));
        if (!Array.isArray(d.rooms) || d.rooms.length === 0) setRoom('0');
      }
    } catch {
      setDead('Could not reach the hotel. Please check your internet and open the link again.');
    } finally {
      setLoading(false);
    }
  }, [api, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  const left = info ? minutesLeft(info.expiresAt) : 0;
  useEffect(() => {
    // The server is the judge, but the page should not sit on a dead link.
    if (info && left === 0) setDead('This link has expired (links last 30 minutes). Please ask reception for a new one.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, left]);

  const isAadhaar = idType === 'Aadhar Card';
  const complete = info && Number(info.received) >= Number(info.requiredIds);

  const upload = async () => {
    setErr('');
    if (room === '') { setErr('Please choose your room first.'); return; }
    if (!front) { setErr('Please add a photo of the front of your ID.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('RoomNumber', String(room));
      fd.append('IdType', idType);
      if (name.trim()) fd.append('GuestName', name.trim());
      if (idNumber.trim()) fd.append('IdNumber', idNumber.trim());
      fd.append('Front', front);
      if (back) fd.append('Back', back);
      const r = await fetch(`${api}/guestid/${encodeURIComponent(token)}/upload`, { method: 'POST', body: fd });
      const d = await r.json().catch(() => ({}));
      if (d?.valid === false) { setDead(d.message || 'This link is not valid any more.'); return; }
      if (!d?.success) {
        setErr(d?.message || 'Could not upload. Please try again.');
        // The same ID again: drop what was chosen so the next tap is a new ID.
        if (d?.duplicate) { setFront(null); setBack(null); setName(''); setIdNumber(''); }
        return;
      }
      setLast(d);
      setFix(null);
      setFront(null); setBack(null); setName(''); setIdNumber('');
      setInfo((p) => (p ? { ...p, received: d.received, requiredIds: d.requiredIds } : p));
      // Refresh the room list so the room now shows a name and a count.
      load();
    } catch {
      setErr('Could not upload. Please check your internet and try again.');
    } finally {
      setBusy(false);
    }
  };

  const saveFix = async () => {
    if (!last || !fix) return;
    setBusy(true);
    try {
      const r = await fetch(`${api}/guestid/${encodeURIComponent(token)}/fix`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId: last.docId, name: fix.name, idNumber: fix.idNumber }),
      });
      const d = await r.json().catch(() => ({}));
      if (d?.success) { setLast((p) => ({ ...p, name: d.name, idNumber: d.idNumber })); setFix(null); load(); }
      else setErr(d?.message || 'Could not save the correction.');
    } catch {
      setErr('Could not save the correction.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Shell><Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box></Shell>;
  }

  if (dead) {
    return (
      <Shell>
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, mb: 1 }}>This link cannot be used</Typography>
        <Typography color='text.secondary' sx={{ mb: 2 }}>{dead}</Typography>
        <Typography variant='body2' color='text.secondary'>
          Please ask the reception desk to send you a new link, or show your ID at the desk.
        </Typography>
      </Shell>
    );
  }

  const rooms = Array.isArray(info.rooms) ? info.rooms : [];
  const pct = info.requiredIds > 0 ? Math.min(100, Math.round((info.received / info.requiredIds) * 100)) : 0;

  return (
    <Shell>
      {/* who / where */}
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--hd-text-secondary, #64748b)', letterSpacing: 0.5 }}>
        {info.hotelName || 'Hotel'}
      </Typography>
      <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: 'var(--hd-text-primary, #0f172a)', lineHeight: 1.2 }}>
        Hello{info.guestFirstName ? ` ${info.guestFirstName}` : ''}, please upload your ID
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
        Booking #{info.bookingId} {'·'} link valid for <b>{left} min</b>
      </Typography>

      {/* progress of the whole group */}
      <Box sx={{ mt: 2, mb: 2.5 }}>
        <Stack direction='row' justifyContent='space-between' sx={{ mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 700 }}>IDs received</Typography>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, color: complete ? 'var(--hd-success, #047857)' : 'var(--hd-text-primary, #0f172a)' }}>
            {info.received} of {info.requiredIds}
          </Typography>
        </Stack>
        <LinearProgress variant='determinate' value={pct}
          sx={{ height: 8, borderRadius: 4, bgcolor: '#E2E8F0', '& .MuiLinearProgress-bar': { bgcolor: complete ? 'var(--hd-success, #047857)' : 'var(--hd-info, #1a56db)' } }} />
      </Box>

      {/* who is in which room, and whose ID is still missing */}
      {rooms.length > 0 && (
        <Paper variant='outlined' sx={{ p: 1.25, mb: 2, borderRadius: 2 }}>
          <Typography sx={{ fontSize: '0.84rem', fontWeight: 800, mb: 0.5 }}>Who is in which room</Typography>
          {rooms.map((r) => (
            <Stack key={r.roomNumber} direction='row' justifyContent='space-between' alignItems='center' sx={{ py: 0.4, borderTop: '1px solid var(--hd-border, #f1f5f9)' }}>
              <Typography sx={{ fontSize: '0.86rem' }}>
                <b>Room {r.roomNumber}</b>{r.occupantName ? ` \u00B7 ${r.occupantName}` : ' \u00B7 not allotted yet'}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: r.idCount > 0 ? 'var(--hd-success, #047857)' : 'var(--hd-warning, #b45309)' }}>
                {r.idCount > 0 ? `${r.idCount} ID${r.idCount > 1 ? 's' : ''} \u2713` : 'ID needed'}
              </Typography>
            </Stack>
          ))}
        </Paper>
      )}

      {complete && (
        <Alert severity='success' sx={{ mb: 2 }}>
          All {info.requiredIds} IDs are in. Thank you! You can still add another if the desk asks for it.
        </Alert>
      )}

      {/* the ID just saved - check and fix */}
      {last && (
        <Paper variant='outlined' sx={{ p: 1.5, mb: 2.5, borderColor: '#A7F3D0', bgcolor: '#ECFDF5', borderRadius: 2 }}>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 0.75 }}>
            <CheckCircleIcon sx={{ color: 'var(--hd-success, #047857)' }} />
            <Typography sx={{ fontWeight: 800, color: '#065F46' }}>ID saved</Typography>
          </Stack>
          {fix ? (
            <Stack spacing={1}>
              <TextField size='small' label='Name on the ID' value={fix.name}
                onChange={(e) => setFix((p) => ({ ...p, name: e.target.value }))} />
              <TextField size='small' label='ID number' value={fix.idNumber}
                onChange={(e) => setFix((p) => ({ ...p, idNumber: e.target.value }))} />
              <Stack direction='row' spacing={1}>
                <Button variant='contained' size='small' onClick={saveFix} disabled={busy} sx={{ ...GREEN, py: 0.8, fontSize: '0.9rem' }}>Save</Button>
                <Button size='small' onClick={() => setFix(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
              </Stack>
            </Stack>
          ) : (
            <>
              <Typography sx={{ fontSize: '0.95rem' }}>
                <b>{last.name || 'Name not read'}</b>
                {last.idNumber ? ` · ${last.idType}: ${last.idNumber}` : ` · ${last.idType}`}
                {last.roomNumber ? ` · Room ${last.roomNumber}` : ''}
              </Typography>
              {last.ocrNote && (
                <Typography sx={{ fontSize: '0.82rem', color: 'var(--hd-warning, #b45309)', mt: 0.5 }}>{last.ocrNote}</Typography>
              )}
              <Button size='small' onClick={() => setFix({ name: last.name || '', idNumber: last.idNumber || '' })}
                sx={{ textTransform: 'none', fontWeight: 700, mt: 0.5, px: 0 }}>
                {last.name ? 'Wrong name or number? Fix it' : 'Type the name'}
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* step 1: room */}
      {rooms.length > 1 && (
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, mb: 1 }}>1. Which room are you staying in?</Typography>
          <Stack direction='row' flexWrap='wrap' gap={1}>
            {rooms.map((r) => {
              const on = String(r.roomNumber) === room;
              return (
                <Chip key={r.roomNumber} clickable icon={<BedOutlinedIcon sx={{ color: on ? '#fff !important' : undefined }} />}
                  label={`Room ${r.roomNumber}${r.occupantName ? ` · ${r.occupantName.split(' ')[0]}` : ''}${r.idCount ? ` (${r.idCount})` : ''}`}
                  onClick={() => setRoom(String(r.roomNumber))}
                  sx={{ height: 40, px: 0.5, fontSize: '0.92rem', fontWeight: 700,
                    bgcolor: on ? 'var(--hd-info, #1a56db)' : 'var(--hd-surface, #ffffff)', color: on ? '#fff' : 'var(--hd-text-primary, #0f172a)',
                    border: '1px solid', borderColor: on ? 'var(--hd-info, #1a56db)' : 'var(--hd-border, #cbd5e1)' }} />
              );
            })}
          </Stack>
        </Box>
      )}
      {rooms.length === 1 && (
        <Typography sx={{ fontSize: '0.9rem', mb: 2 }}>
          <BedOutlinedIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'text-bottom' }} />
          Room <b>{rooms[0].roomNumber}</b>
        </Typography>
      )}
      {rooms.length === 0 && (
        <Alert severity='info' sx={{ mb: 2 }}>Your room will be given at the desk. Your ID is still saved to your booking.</Alert>
      )}

      {/* step 2: the ID */}
      <Typography sx={{ fontSize: '0.9rem', fontWeight: 800, mb: 1 }}>
        {rooms.length > 1 ? '2. ' : ''}Add a photo of the ID
      </Typography>
      <Stack spacing={1.25}>
        <TextField select size='small' label='ID type' value={idType} onChange={(e) => setIdType(e.target.value)}>
          {(info.idTypes || ['Aadhar Card']).map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
        <PhotoPick label='Front side' file={front} onPick={setFront} required />
        <PhotoPick label='Back side' file={back} onPick={setBack} required={false} />
        {isAadhaar ? (
          <Typography variant='caption' color='text.secondary'>
            We read the name and number from the Aadhaar automatically. Add the back side for a better read.
          </Typography>
        ) : (
          <>
            <TextField size='small' label='Name on the ID (optional)' value={name} onChange={(e) => setName(e.target.value)} />
            <TextField size='small' label='ID number (optional)' value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
          </>
        )}
        {err && <Alert severity='error'>{err}</Alert>}
        <Button fullWidth variant='contained' sx={GREEN} onClick={upload}
          disabled={busy || !front || room === ''}>
          {busy ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (last ? 'Upload another ID' : 'Upload ID')}
        </Button>
      </Stack>

      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 2.5 }}>
        Your ID is stored only with this booking at {info.hotelName || 'the hotel'}. Each adult in the group can use this same link.
      </Typography>
    </Shell>
  );
}
